/**
 * AI TeamMate mock responses — INTERNAL ALPHA TESTING ONLY.
 *
 * Gated in the UI to authorizationStore.getters.isInternal. When mock mode is on, the chat client
 * short-circuits the /api/teammate/chat call and returns one of these canned `data` objects instead,
 * shaped exactly like the real response envelope (response, actions, proposals, pendingActions,
 * formPills, formFill, view, usage). Purpose: exercise every interaction's card/UI without the
 * backend producing each scenario. Keyword-driven so a tester can summon a specific interaction;
 * the default shows a representative mix. Remove this file (and its toggle) before GA.
 */

// A small sample workflow module so the design/modify diagram renders (states + activities + flows).
// Shaped exactly like the design backend's proposal schema: the module icon under `icon`; fields
// keyed `name` with a display-string `type`; states/activities carry NO id; flows reference states
// and activities BY NAME (from/to/activity). The teammate card normalizes this for rendering
// (FTSClientTeammate.normalizeSchema) — keeping the mock in the real shape ensures mock mode exercises
// that path instead of masking shape bugs.
const SAMPLE_MODULE = {
  name: "Leave Request",
  icon: "🏖️",
  description: "Track employee leave requests from draft through to a manager's decision.",
  information: [
    { name: "Employee", type: "User", ai_hint: "The employee requesting leave." },
    { name: "Start Date", type: "Date", ai_hint: "First day of leave." },
    { name: "End Date", type: "Date", ai_hint: "Last day of leave." },
    { name: "Reason", type: "MultiText", ai_hint: "Why the leave is needed." },
  ],
  states: [
    { name: "Draft", color: "#94a3b8", initial: true, ai_hint: "Being prepared, not yet submitted." },
    { name: "Pending Approval", color: "#f59e0b", initial: false, ai_hint: "Awaiting a manager's decision." },
    { name: "Approved", color: "#16a34a", initial: false, ai_hint: "Leave has been granted." },
    { name: "Rejected", color: "#dc2626", initial: false, ai_hint: "Leave was declined." },
  ],
  activities: [
    { name: "Create", actor: "ai", fields: [], ai_hint: "Open a new draft request." },
    { name: "Submit", actor: "hybrid", confidence_threshold: 0.8,
      fields: [
        { name: "Employee", required: true }, { name: "Start Date", required: true },
        { name: "End Date", required: true }, { name: "Reason", required: false },
      ],
      ai_hint: "Send the draft for approval." },
    { name: "Approve", actor: "human", fields: [], ai_hint: "Approve the request." },
    { name: "Reject", actor: "human", fields: [], ai_hint: "Reject the request." },
  ],
  flows: [
    { from: "Draft", to: "Pending Approval", activity: "Submit" },
    { from: "Pending Approval", to: "Approved", activity: "Approve" },
    { from: "Pending Approval", to: "Rejected", activity: "Reject" },
  ],
};

// SAMPLE_MODULE + a Cancelled state / Cancel activity / Manager field, minus Reject — so a modify
// proposal shows added (states/activities/fields) and removed (Reject) in the diff and diagram.
// Same name-keyed backend shape as SAMPLE_MODULE (filters/flows reference activities by name).
const SAMPLE_MODULE_MODIFIED = {
  name: SAMPLE_MODULE.name,
  icon: SAMPLE_MODULE.icon,
  description: SAMPLE_MODULE.description,
  information: SAMPLE_MODULE.information.concat([{ name: "Manager", type: "User", ai_hint: "The approving manager." }]),
  states: SAMPLE_MODULE.states.concat([{ name: "Cancelled", color: "#6b7280", initial: false, ai_hint: "Request withdrawn before a decision." }]),
  activities: SAMPLE_MODULE.activities
    .filter((a) => a.name !== "Reject")
    .concat([{ name: "Cancel", actor: "hybrid", fields: [], confidence_threshold: 0.7, ai_hint: "Withdraw the request." }]),
  flows: SAMPLE_MODULE.flows
    .filter((f) => f.activity !== "Reject")
    .concat([{ from: "Pending Approval", to: "Cancelled", activity: "Cancel" }]),
};

function mockUsage() {
  return {
    inputTokens: 1280, outputTokens: 420, cacheReadInputTokens: 900,
    toolCalls: 2, latencyMs: 640, cost: 0, model: "mock-model",
  };
}

function mockAi() {
  return { reasoning: "Mock reasoning for alpha testing.", confidence: 0.92, model: "mock-model" };
}

// --- per-interaction builders ------------------------------------------------

function doneAction() {
  return {
    type: "activity", activity: "create", module: "Applicant",
    entryId: 10012, documentId: "PPL00012", state: "Draft",
    input: {
      "Name": "David Okafor",
      "Email": "david.okafor@email.com",
      "Employer": "Meridian Logistics Berhad",
      "Occupation": "Regional Procurement Manager",
      "Years Employed": 9,
      "Annual Income": 82000,
    },
    nextActivities: [
      { id: "submitApplication", name: "Submit Application", standard: false },
      { id: "edit", name: "Edit", standard: true },
      { id: "delete", name: "Delete", standard: true },
      { id: "changeState", name: "Change State", standard: true },
    ],
    ai: mockAi(), flagged: false,
  };
}

// GNey's access card (pendingActions[] type "access"): the server's dry run of a proposed rule
// change, which only the user's Approve applies (configure-access proposal §13.7).
function accessCard() {
  return {
    type: "access", kind: "access", module: "Expense Claim", moduleId: 5120,
    request: {
      kind: "access",
      access: {
        activities: { Approve: { allow: [{ profileField: "Position", in: ["Manager"] }] } },
        fields: { Items: { columns: { "Unit Cost": "everyone" } } },
      },
    },
    changes: [
      { item: "activities.Approve", before: "Everyone", after: "Only people whose Employee › Position is Manager", widens: false },
      { item: "fields.Items.columns.Unit Cost", before: "No one (platform administrators still can)", after: "Everyone", widens: true },
    ],
    warnings: [{
      code: "self_elevation", item: "activities.Approve",
      message: "Employee › Position decides who can run Approve, and members can change their own Position through Edit in Employee, so someone could give themselves access.",
    }],
    widens: true,
  };
}

function hybridPending() {
  return {
    type: "activity", module: "Job Log", activity: "submit", activityId: "submit",
    entryId: 77576, documentId: "JB 77576",
    // 5 prepared values on purpose: the Activity card shows the first 3 and folds the rest
    // behind "View more" (design) — fewer than 4 and the fold never renders in mock mode.
    input: {
      "Comment": "Looks complete — submitting for review.", "Reviewer": { value: "Mary Lim", id: 12 },
      "Due": "2026-07-03", "Priority": "High", "Site": "Block C rooftop",
    },
    values: {
      comment: "Looks complete — submitting for review.", reviewerId: 12, reviewer: "Mary Lim",
      due: "2026-07-03", priority: "High", site: "Block C rooftop",
    },
    state: "Pending Approval", comment: null, assignees: null, due: "2026-07-03",
    ai: mockAi(), actor: "hybrid",
  };
}

function formPill() {
  return {
    type: "activity", module: "Leave Request", activity: "Approve", activityId: "approve",
    entryId: 4021, actor: "human",
    // `input` (display-name keyed) drives the card's value rows — the AI prefilled the comment;
    // Decision is left for the human. `values` (fieldName-keyed) is the open-form prefill.
    input: { "Approver Comment": "Dates check out against the team roster." },
    values: { approverComment: "Dates check out against the team roster.", decision: "" },
  };
}

// Bulk fan-out — one activity across several entries (the "move these 4 issues" case), so the
// paged group card (‹ n of N › + Confirm all) is exercisable in mock mode. Same item shapes as
// hybridPending()/formPill(); actor decides the variant: "hybrid" → pendingActions (Confirm +
// Confirm all), "human" → formPills (Open form page by page, no Confirm).
const BULK_ISSUES = [
  { entryId: 78895, documentId: "SS 00001", title: "Update user onboarding documentation", assignee: { value: "John Tan", id: 7 } },
  { entryId: 78896, documentId: "SS 00002", title: "Login page times out after 2 minutes of inactivity", assignee: { value: "Mary Lim", id: 12 } },
  { entryId: 78897, documentId: "SS 00003", title: "Export to CSV truncates long text fields", assignee: { value: "Mary Lim", id: 12 } },
  { entryId: 78898, documentId: "SS 00004", title: "Add dark mode support", assignee: { value: "Yang Yu Chen", id: 3 } },
];

function bulkPickUps(actor) {
  return BULK_ISSUES.map((it) => ({
    type: "activity", module: "Issue Tracker", activity: "Pick Up", activityId: "pickup",
    entryId: it.entryId, documentId: it.documentId, actor,
    input: { "Issue Title": it.title, "Assignee": it.assignee },
    values: { issueTitle: it.title, assignee: it.assignee.value, assigneeId: it.assignee.id },
    state: "In Progress",
    ai: mockAi(),
  }));
}

function askQuestions() {
  // Mirrors AITeammateService.BuildQuestions output: 1-4 questions, each with a short header
  // (<= 12 chars), question text, a multiSelect flag, and 2-4 options carrying positional ids
  // (q_0/o_0…) and an optional description. One single-select (with descriptions) + one
  // multi-select so the card exercises single/multi picks, option descriptions and the
  // "something else" free-text path in one turn.
  return [
    {
      id: "q_0",
      header: "Priority",
      question: "What priority should the new Job Log entry have?",
      multiSelect: false,
      options: [
        { id: "o_0", label: "High", description: "Needs attention today." },
        { id: "o_1", label: "Medium", description: "Handle within this week." },
        { id: "o_2", label: "Low", description: "Whenever there's time." },
      ],
    },
    {
      id: "q_1",
      header: "Notify",
      question: "Who should be notified once it's submitted?",
      multiSelect: true,
      options: [
        { id: "o_0", label: "Reviewer" },
        { id: "o_1", label: "Manager" },
        { id: "o_2", label: "Requester" },
      ],
    },
  ];
}

function designProposal() {
  return {
    // message is empty on purpose: the card body already renders the state-flow summary line
    // (design) — a message repeating it would show twice. modifyProposal keeps one to exercise
    // the message row.
    type: "module", kind: "design", phase: "proposed", module: SAMPLE_MODULE.name, success: true,
    schema: SAMPLE_MODULE, message: "",
    validation: { valid: true, errors: [], warnings: ["Activity 'Create' is not referenced by any flow."], summary: null },
    ai: mockAi(),
  };
}

function modifyProposal() {
  return {
    // message is empty like designProposal's: the card body is identity + flow line only —
    // what changed is described in the response PROSE (see mockChat's modify branch).
    type: "module", kind: "modify", phase: "proposed", module: SAMPLE_MODULE.name, success: true,
    proposedSchema: SAMPLE_MODULE_MODIFIED,
    currentSchema: SAMPLE_MODULE,
    message: "",
    validation: { valid: true, errors: [], warnings: [], summary: null },
    ai: mockAi(),
  };
}

function formFill() {
  // Direct open-form write (fieldName-keyed). In mock mode the chat client opens this target form
  // first (mockOpenThenFill), then writes — so the fields populate without the tester having
  // pre-opened it. Needs the target module to exist in the workspace to be visible.
  // fields[] mirrors the server's per-row review shape (label + current -> proposed + keys) so
  // the Form card exercises the old → new value rendering (End Date shows a change).
  return {
    module: "Leave Request", activityId: "submit", entryId: 4021,
    values: { startDate: "2026-07-03", endDate: "2026-07-08", reason: "Family event" },
    fields: [
      { fieldName: "startDate", label: "Start Date", proposed: "2026-07-03", keys: ["startDate"] },
      { fieldName: "endDate", label: "End Date", current: "2026-07-05", proposed: "2026-07-08", keys: ["endDate"] },
      { fieldName: "reason", label: "Reason", proposed: "Family event", keys: ["reason"] },
    ],
    note: "Filled the dates and reason from your message.",
  };
}

// --- mock module + form (for self-contained open-form / fill testing) -------
// Fake modules the open-form path can resolve to. Deliberately NOT injected into the global
// chatModules() list — they're resolved only when the teammate opens a form (decorateQuickViewData),
// so reference chips (module/entry/listing) keep their normal behaviour and nothing leaks app-wide.
const MOCK_MODULES = [
  { id: 990001, name: "Leave Request", emoji: "🏖️" },
  { id: 990002, name: "Job Log", emoji: "🛠️" },
  { id: 990003, name: "Issue Tracker", emoji: "🐞" },
  { id: 990004, name: "Applicant", emoji: "📄" },
];

/** Resolve a mock module by display name (case/space/punctuation-insensitive), or null. */
function resolveMockModule(name) {
  if (!name) return null;
  const norm = (s) => String(s == null ? "" : s).toLowerCase().replace(/[^a-z0-9]/g, "");
  const key = norm(name);
  return MOCK_MODULES.find((m) => norm(m.name) === key) || null;
}

// Field-item factory in the shape FTSForm/FTSFormElement render: `type` is the numeric
// informationTypes id (Text=0, Number=3, Date=5, Multiline=16), `input` its string form. ids are
// unique within one form (FTSForm keys on item.id); they may repeat across opens since each call
// deep-clones its row set.
let _mockFieldUid = 0;
function field(type, input, fieldName, displayName) {
  _mockFieldUid += 1;
  return { id: `mockf-${_mockFieldUid}`, type, input, fieldName, displayName, required: false };
}

// Per (module:activity) field layouts. fieldNames line up with the fill values in formFill() so the
// direct-write highlight lands on real fields. Falls back to a generic note/value form.
const MOCK_FORM_FIELDS = {
  "Leave Request:submit": () => [
    field(0, "text", "employee", "Employee"),
    field(5, "date", "startDate", "Start Date"),
    field(5, "date", "endDate", "End Date"),
    field(16, "textArea", "reason", "Reason"),
  ],
  "Leave Request:approve": () => [
    field(16, "textArea", "approverComment", "Approver Comment"),
    field(0, "text", "decision", "Decision"),
  ],
  "Job Log:submit": () => [
    field(16, "textArea", "comment", "Comment"),
    field(0, "text", "reviewer", "Reviewer"),
    field(5, "date", "due", "Due"),
  ],
  "Issue Tracker:pickup": () => [
    field(0, "text", "issueTitle", "Issue Title"),
    field(0, "text", "assignee", "Assignee"),
  ],
};
const GENERIC_FORM_FIELDS = () => [
  field(0, "text", "title", "Title"),
  field(16, "textArea", "notes", "Notes"),
];

function mockDocId(module, entryId) {
  const prefix = module === "Leave Request" ? "LV" : module === "Job Log" ? "JB" : module === "Issue Tracker" ? "SS" : "MK";
  return entryId != null ? `${prefix} ${entryId}` : `${prefix} (new)`;
}

/**
 * Build a mock /api/activity/form response (the `res.data` shape classificationStore.loadActivityForm
 * consumes) so a real, fillable form renders for a mock module without a backend. Seeds the form
 * mostly blank so a subsequent fill_form is what visibly populates + highlights it.
 */
function mockActivityForm(opts) {
  const o = opts || {};
  const module = o.module || "";
  const activityId = o.activityId || "create";
  const entryId = o.entryId != null ? o.entryId : null;
  const vectorId = o.vectorId != null ? o.vectorId : null;
  const build = MOCK_FORM_FIELDS[`${module}:${activityId}`] || GENERIC_FORM_FIELDS;
  const activityName = String(activityId).charAt(0).toUpperCase() + String(activityId).slice(1);
  // Seed a non-fill field so the form isn't entirely empty; the fill targets the blank ones.
  const seed = `${module}:${activityId}` === "Leave Request:submit" ? { employee: "Yang Yu Chen" } : {};
  return {
    // Echoed for FTSQuickView to synthesize its `module` (no real workspace vector behind a mock).
    moduleName: module,
    emoji: o.emoji,
    header: {
      id: entryId || undefined,
      entryId: entryId || undefined,
      documentId: mockDocId(module, entryId),
      moduleId: vectorId || undefined,
      activity: { id: activityId, name: activityName },
    },
    footer: {},
    classificationForm: {
      design: { rows: [{ items: build() }] },
      default: seed,
      needSync: false,
      nativeBundle: null,
    },
  };
}

// --- entry points ------------------------------------------------------------

/**
 * Build a mock /chat response from the user's message. Keyword-driven; default returns a mix that
 * exercises chips + a done card + a hybrid card + a form pill in one turn.
 */
function mockChat(message, opts) {
  const m = String(message || "").toLowerCase();
  const data = { response: "", actions: [], proposals: [], pendingActions: [], formPills: [], formFill: [], questions: [], view: null, ai_instruction: null, usage: mockUsage() };

  // First: "who can access the module" also says "module".
  if (m.includes("access") || m.includes("permission")) {
    data.response = "[mock] Only managers would approve claims, and Unit Cost would open to everyone — "
      + "that part lets in people who are kept out today. Approve the card to apply it.";
    data.pendingActions = [accessCard()];
    return data;
  }
  if (m.includes("modify") || m.includes("change module") || m.includes("update module")) {
    // The change summary lives HERE, in the prose — the proposal card below stays minimal
    // (identity + flow line; full detail in the diagram).
    data.response = "[mock] Proposed changes to {{Leave Request}}: adds a **Cancelled** state, "
      + "a **Cancel** activity and a **Manager** field; removes **Reject**. "
      + "Review the diagram, then Apply changes.";
    data.proposals = [modifyProposal()];
    return data;
  }
  if (m.includes("module") || m.includes("design")) {
    data.response = "[mock] Drafted a module — review the diagram (hover a state/activity), then Create.";
    data.proposals = [designProposal()];
    return data;
  }
  // Bulk fan-out first — "bulk pill(s)"/"bulk human" would otherwise fall into the single-card
  // branches below. "bulk" alone mocks the hybrid batch (paged group + Confirm all).
  if (m.includes("bulk") || m.includes("batch")) {
    const human = m.includes("pill") || m.includes("human") || m.includes("open");
    if (human) {
      data.response = "[mock] Four Pick Up forms are yours to complete — page through and open each.";
      data.formPills = bulkPickUps("human");
    } else {
      data.response = "[mock] Prepared Pick Up on all 4 issues — review page by page and Confirm each, or Confirm all.";
      data.pendingActions = bulkPickUps("hybrid");
    }
    return data;
  }
  if (m.includes("hybrid") || m.includes("confirm")) {
    data.response = "[mock] Prepared an activity for your confirmation. Review the values, Confirm or Open form.";
    data.pendingActions = [hybridPending()];
    return data;
  }
  if (m.includes("pill") || m.includes("human") || m.includes("open form")) {
    data.response = "[mock] This activity is yours to complete — open the form.";
    data.formPills = [formPill()];
    return data;
  }
  if (m.includes("fill")) {
    data.response = "✓ [mock] Opened the Leave Request form and filled Start Date, End Date and Reason.";
    data.formFill = [formFill()];
    return data;
  }
  if (m.includes("option") || m.includes("question") || m.includes("ask")) {
    data.response = "[mock] A couple of quick questions before I continue — predefined single-choice answers continue automatically; use Send answers for multi-select or typed custom answers.";
    data.questions = askQuestions();
    return data;
  }
  if (m.includes("done") || m.includes("create") || m.includes("edit") || m.includes("do it")) {
    data.response = "[mock] Done — see the receipt below.";
    data.actions = [doneAction()];
    return data;
  }
  if (m.includes("chip") || m.includes("list") || m.includes("show")) {
    data.response = [
      "[mock] Here are the 2 entries of {{Job Log}}:",
      "",
      "| ID | Title | Assignee |",
      "| --- | --- | --- |",
      "| {{Job Log:77576:JB 19943}} | Roof inspection | John Tan |",
      "| {{Job Log:77580:JB 19951}} | Safety checklist | Mei Lee |",
      "",
      "You can {{Job Log:77576:edit:Edit JB 19943}}.",
      "",
      // Listing chip in the design's "«module» | «listing»" form; the module-agnostic
      // placeholder variant ({{module:listing:…}}) renders the listing name alone.
      "You're looking at the {{Job Log:listing:2d5791d0-d05c-4e76-8c5f-e509cce1b89c:💬 All}} view, " +
      "also available as {{module:listing:2d5791d0-d05c-4e76-8c5f-e509cce1b89c:💬 All}}.",
    ].join("\n");
    return data;
  }

  // Default: a representative mix so every new card type is visible at once. The hybrid and pill
  // cards render side by side here — same Activity card, pill just has no Confirm.
  data.response =
    "[mock] Here's a bit of everything. Referenced {{Job Log:77576:JB 19943}}. " +
    "I made one edit, prepared one activity for your confirmation (Confirm or Open form), " +
    "and prefilled one that's yours to open, complete and submit — same card, no Confirm.\n\n" +
    "_Tip: type \"module\", \"modify\", \"hybrid\", \"pill\", \"bulk\", \"bulk pills\", \"fill\", \"ask\", \"done\" or \"chips\" to mock a specific interaction._";
  data.actions = [doneAction()];
  data.pendingActions = [hybridPending()];
  data.formPills = [formPill()];
  return data;
}

/**
 * Build a mock commit response for a confirmed pending action / proposal. Accepts one
 * confirmation or an array (Confirm all posts every remaining item in one call) — the array
 * form mirrors the live backend: one result per confirmation, in order, receipt prose one
 * line per item.
 */
function mockConfirm(conf) {
  if (Array.isArray(conf)) {
    const merged = { response: "", actions: [], proposals: [], pendingActions: [], formPills: [], formFill: [], view: null, ai_instruction: null, usage: mockUsage() };
    const lines = [];
    conf.forEach((c) => {
      const one = mockConfirm(c);
      merged.actions = merged.actions.concat(one.actions);
      if (one.response) lines.push(one.response);
    });
    merged.response = lines.join("\n\n");
    return merged;
  }
  const data = { response: "", actions: [], proposals: [], pendingActions: [], formPills: [], formFill: [], view: null, ai_instruction: null, usage: mockUsage() };
  if (conf && conf.type === "access") {
    data.response = `[mock] Access rules of {{${conf.module}}} changed.`;
    data.actions = [{
      type: "access", kind: conf.kind, success: true, module: conf.module, moduleId: conf.moduleId,
      changes: conf.changes, message: `[mock] Access rules of '${conf.module}' changed.`,
    }];
    return data;
  }
  if (conf && conf.type === "module") {
    const modify = conf.kind === "modify";
    const name = (conf.schema && conf.schema.name) || conf.module || "Module";
    // Receipt prose mirrors the live backend: "Module {{Name}} created." — the token renders
    // as the module chip, and being response prose it persists into (and restores from) history.
    data.response = `[mock] Module {{${name}}} ${modify ? "updated" : "created"}.`;
    data.actions = [{
      type: "module", kind: conf.kind || "design", success: true,
      module: name,
      moduleId: 9999, message: `[mock] Module ${modify ? "updated" : "created"} and live.`,
    }];
    return data;
  }
  // activity confirmation — the commit result carries `success: true` (CommitActivityConfirmationAsync),
  // unlike a directly-submitted action which omits it. `message` is the short receipt headline the
  // morphed card titles itself with; the prose references the entry inline as a chip (design).
  const module = (conf && conf.module) || "Job Log";
  const entryId = (conf && conf.entryId) || 77576;
  const docId = (conf && conf.documentId) || "JB 10082";
  // The confirmed item's target state rides on the confirmation; default keeps the classic
  // Job Log "Submitted for Review" receipt for the single-card mock.
  const state = (conf && conf.state) || "Pending Approval";
  const headline = state === "Pending Approval" ? "Submitted for Review" : `Moved to ${state}`;
  data.response = `[mock] {{${module}:${entryId}:${docId}}} has been confirmed. Status updated to ${state}.`;
  data.actions = [{
    type: "activity", success: true, activity: (conf && conf.activity) || "submit", module,
    entryId, documentId: docId, state, message: headline,
    input: (conf && conf.input) || {}, availableActivities: { custom: [], standard: ["edit", "comment"] },
    ai: mockAi(), flagged: false,
  }];
  return data;
}

// --- conversation history (session picker) mocks ----------------------------
// Canned past-conversation list + detail, so the history dropdown is testable in mock mode without
// the /api/teammate/sessions backend. Shapes mirror the agreed REST contract: the list returns
// { sessionId, title, updatedAt, turnCount }; detail returns { sessionId, title, messages }, each
// message in chat.history shape (role/content/usage/...). Remove with the rest of the mock at GA.
function ago(ms) {
  return new Date(Date.now() - ms).toISOString();
}
const MOCK_SESSIONS = [
  { sessionId: "mock-sess-1", title: "Summarize this Job Log entry", lastTurnAt: ago(60 * 1000), turnCount: 4 },
  { sessionId: "mock-sess-2", title: "Design a Leave Request module", lastTurnAt: ago(3 * 3600 * 1000), turnCount: 6 },
  { sessionId: "mock-sess-3", title: "What needs my attention today?", lastTurnAt: ago(26 * 3600 * 1000), turnCount: 2 },
  { sessionId: "mock-sess-4", title: "Bulk-close stale tickets", lastTurnAt: ago(5 * 24 * 3600 * 1000), turnCount: 9 },
];

function mockSessions() {
  // Return a shallow clone so the UI mutating titles/order can't corrupt the canned source.
  return { page: 0, pageSize: 50, totalItems: MOCK_SESSIONS.length, sessions: MOCK_SESSIONS.map((s) => ({ ...s })) };
}

// Mirrors GET /api/teammate/sessions/{id}/turns: append-only turns, each a user->assistant exchange
// with per-turn usage counts (inputTokens/outputTokens/...). Only the conversation record — prose,
// receipts (actionsPerformed), usage; interaction cards are live-turn surfaces and never persist.
function mockSessionDetail(sessionId) {
  const meta = MOCK_SESSIONS.find((s) => s.sessionId === sessionId) || MOCK_SESSIONS[0];
  const u = mockUsage();
  return {
    sessionId: meta.sessionId,
    title: meta.title,
    turns: [
      {
        turnNumber: 1,
        userMessage: meta.title,
        assistantResponse: `[mock] Resumed conversation "${meta.title}". This is a restored turn — ask a follow-up to continue it.`,
        actionsPerformed: [],
        model: u.model, inputTokens: u.inputTokens, outputTokens: u.outputTokens,
        cacheReadInputTokens: u.cacheReadInputTokens, toolCalls: u.toolCalls, costUsd: 0, latencyMs: u.latencyMs,
      },
    ],
  };
}

export default { mockChat, mockConfirm, resolveMockModule, mockActivityForm, mockSessions, mockSessionDetail };
